import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { api } from '../../Api';
import { useDeveloperAccess } from '../../DeveloperAccess';
import { getError } from '../../utils';

/**
 * MESSAGE TEMPLATES — every SMS / WhatsApp text the server can send.
 *
 * One card per template and channel: switch it on/off, edit the text, preview
 * it with sample values, send a test, and open its own log and statistics.
 * The live-status table above the cards is the same switches as a grid.
 * The master switch at the top stops ALL outgoing SMS or WhatsApp at once.
 */

const C = {
  maroon: '#7f2a2a',
  maroonDark: '#6b2020',
  border: '#e5e7eb',
  muted: '#6b7280',
  text: '#111827',
  bg: '#f8fafc',
  sms: { fg: '#7c3aed', bg: '#f3e8ff' },
  whatsapp: { fg: '#15803d', bg: '#dcfce7' },
  group: { fg: '#4f46e5', bg: '#eef2ff' },
};

const CHANNEL_LABEL = { sms: 'SMS', whatsapp: 'WhatsApp' };
const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'ok', label: 'Sent' },
  { key: 'failed', label: 'Failed' },
  { key: 'blocked', label: 'Blocked / skipped' },
];

const fmt = (n) => Number(n || 0).toLocaleString('en-US');
const when = (d) => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const smsParts = (text) => {
  const s = String(text || '');
  // Any non-GSM character (Bangla, ৳, emoji) switches SMS to 70-char Unicode parts.
  const unicode = /[^\x20-\x7E\n\r]/.test(s);
  const single = unicode ? 70 : 160;
  const multi = unicode ? 67 : 153;
  return { chars: s.length, parts: s.length <= single ? 1 : Math.ceil(s.length / multi), unicode };
};
const fill = (text, vars) => String(text || '').replace(/\{(\w+)\}/g, (m, k) => (vars?.[k] === undefined ? m : String(vars[k])));

function Toggle({ on, onClick, disabled, size = 'md' }) {
  const w = size === 'sm' ? 44 : 52;
  const h = size === 'sm' ? 24 : 28;
  const k = h - 6;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={on}
      style={{ width: w, height: h, borderRadius: 999, border: on ? 'none' : `1px solid ${C.border}`, position: 'relative', cursor: disabled ? 'wait' : 'pointer', padding: 2, background: on ? C.maroon : '#e5e7eb', flexShrink: 0, transition: 'background 0.15s' }}
    >
      <span style={{ display: 'block', width: k, height: k, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,.2)', transition: 'transform 0.15s', transform: on ? `translateX(${w - k - 6}px)` : 'translateX(0)' }} />
    </button>
  );
}

function ChannelBadge({ channel }) {
  const c = C[channel];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 999, background: c.bg, color: c.fg, fontWeight: 600, fontSize: 13 }}>
      <i className={channel === 'whatsapp' ? 'fab fa-whatsapp' : 'far fa-comment-alt'} aria-hidden="true" />
      {CHANNEL_LABEL[channel]}
    </span>
  );
}

function Pill({ children, fg = C.group.fg, bg = C.group.bg }) {
  return (
    <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 999, background: bg, color: fg, fontWeight: 700, fontSize: 11, letterSpacing: 0.6, textTransform: 'uppercase', whiteSpace: 'nowrap', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {children}
    </span>
  );
}

function OutlineBtn({ icon, children, onClick, disabled }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 10, border: `1px solid ${C.border}`, background: '#fff', color: C.text, fontWeight: 500, fontSize: 14 }}>
      <i className={icon} aria-hidden="true" style={{ color: '#3b82f6' }} />
      {children}
    </button>
  );
}

function Stat({ label, value, sub, tone }) {
  return (
    <div style={{ flex: '1 1 140px', background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: '12px 16px' }}>
      <div style={{ fontSize: 12, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: tone || C.text }}>{value}</div>
      {sub ? <div style={{ fontSize: 12, color: C.muted }}>{sub}</div> : null}
    </div>
  );
}

const statLine = (s) => `${fmt(s?.today?.sent)} today · ${fmt(s?.week?.sent)} in 7d`;

export default function MessageTemplatesScreen() {
  const { devHeaders } = useDeveloperAccess();
  const [data, setData] = useState(null);
  const [busyKey, setBusyKey] = useState('');
  const [channel, setChannel] = useState('all');
  const [group, setGroup] = useState('all');
  const [query, setQuery] = useState('');
  const [tableOpen, setTableOpen] = useState(true);
  const [modal, setModal] = useState(null); // { type: 'edit'|'preview'|'test'|'log', t }

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(api + '/api/developer/message-templates', { headers: devHeaders });
      setData(d);
    } catch (err) {
      toast.error(getError(err));
    }
  }, [devHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const templates = useMemo(() => data?.templates || [], [data]);
  const groups = useMemo(() => [...new Set(templates.map((t) => t.group))], [templates]);
  const counts = useMemo(() => ({
    all: templates.length,
    sms: templates.filter((t) => t.channel === 'sms').length,
    whatsapp: templates.filter((t) => t.channel === 'whatsapp').length,
  }), [templates]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return templates.filter((t) =>
      (channel === 'all' || t.channel === channel)
      && (group === 'all' || t.group === group)
      && (!q || `${t.label} ${t.key} ${t.text} ${t.group}`.toLowerCase().includes(q)));
  }, [templates, channel, group, query]);

  // Live-status rows: one per message, SMS and WhatsApp side by side.
  const rows = useMemo(() => {
    const byBase = new Map();
    for (const t of visible) {
      const r = byBase.get(t.base) || { base: t.base, label: t.label, group: t.group, recipient: t.recipient };
      r[t.channel] = t;
      byBase.set(t.base, r);
    }
    return [...byBase.values()];
  }, [visible]);
  const rowGroups = useMemo(() => {
    const out = [];
    for (const r of rows) {
      let g = out.find((x) => x.group === r.group);
      if (!g) out.push((g = { group: r.group, rows: [] }));
      g.rows.push(r);
    }
    return out;
  }, [rows]);

  const replaceTemplate = (next) =>
    setData((d) => ({ ...d, templates: d.templates.map((t) => (t.key === next.key ? { ...next, stats: t.stats } : t)) }));

  const save = async (t, patch) => {
    setBusyKey(t.key);
    try {
      const { data: d } = await axios.put(api + '/api/developer/message-templates', { key: t.key, ...patch }, { headers: devHeaders });
      replaceTemplate(d.template);
      toast.success(patch.text !== undefined ? 'Template saved.' : `${t.label} (${CHANNEL_LABEL[t.channel]}) ${d.template.enabled ? 'on' : 'off'}.`);
      return true;
    } catch (err) {
      toast.error(getError(err));
      return false;
    } finally {
      setBusyKey('');
    }
  };

  const toggle = (t) => {
    if (t.enabled && t.kind === 'otp') {
      const other = templates.find((x) => x.base === t.base && x.key !== t.key);
      if (other && !other.enabled && !window.confirm(`Both channels of "${t.label}" will be off — members will not get this code at all. Continue?`)) return;
    }
    save(t, { enabled: !t.enabled });
  };

  const saveMaster = async (patch) => {
    if (Object.values(patch).some((v) => v === false) && !window.confirm('Stop ALL of this outgoing messaging now? OTP codes will not be delivered while it is off.')) return;
    setBusyKey('master');
    try {
      const { data: d } = await axios.put(api + '/api/developer/messaging', patch, { headers: devHeaders });
      setData((cur) => ({ ...cur, master: { smsEnabled: d.smsEnabled, whatsappEnabled: d.whatsappEnabled } }));
      toast.success('Master switch saved.');
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setBusyKey('');
    }
  };

  const master = data?.master || { smsEnabled: true, whatsappEnabled: true };
  const totals = data?.totals;
  const onCount = templates.filter((t) => t.enabled).length;

  return (
    <div style={{ background: C.bg, minHeight: '100%', padding: '16px 16px 48px' }}>
      <Helmet>
        <title>Message Templates</title>
      </Helmet>
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, borderBottom: `1px solid ${C.border}`, paddingBottom: 12, marginBottom: 20 }}>Message Templates</h1>

        {/* Gallery header + master switch */}
        <div style={{ background: '#e8edf5', border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, display: 'flex', flexWrap: 'wrap', gap: 20, justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ flex: '1 1 380px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
              <span style={{ width: 40, height: 40, borderRadius: 10, background: '#dbe4ff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#3b5bdb' }}>
                <i className="fas fa-layer-group" aria-hidden="true" />
              </span>
              <span style={{ fontSize: 22, fontWeight: 700 }}>Message Template Gallery</span>
            </div>
            <div style={{ color: '#374151' }}>
              Every SMS and WhatsApp this system sends — OTP codes, member notifications and admin alerts. Edit any text, switch each one on or off, and open its log.
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 260 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Master switch — all outgoing</div>
            {['smsEnabled', 'whatsappEnabled'].map((k) => (
              <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: '8px 12px' }}>
                <ChannelBadge channel={k === 'smsEnabled' ? 'sms' : 'whatsapp'} />
                <span style={{ flex: 1, fontWeight: 600, color: master[k] ? '#15803d' : '#b91c1c' }}>{master[k] ? 'Sending' : 'ALL BLOCKED'}</span>
                <Toggle on={master[k]} disabled={busyKey === 'master'} onClick={() => saveMaster({ [k]: !master[k] })} />
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8 }}>
              <OutlineBtn icon="fas fa-sync-alt" onClick={load}>Refresh</OutlineBtn>
            </div>
          </div>
        </div>

        {!master.smsEnabled || !master.whatsappEnabled ? (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 12, padding: '12px 16px', marginBottom: 16 }}>
            <strong>Master switch:</strong> all outgoing {!master.smsEnabled && !master.whatsappEnabled ? 'SMS and WhatsApp are' : `${!master.smsEnabled ? 'SMS is' : 'WhatsApp is'}`} blocked, whatever the template switches below say.
          </div>
        ) : null}

        {/* Totals */}
        {totals ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
            <Stat label="Templates on" value={`${onCount}/${templates.length}`} />
            <Stat label="Sent today" value={fmt(totals.today.sent)} sub={`${fmt(totals.today.ok)} ok · ${fmt(totals.today.failed)} failed · ${fmt(totals.today.blocked)} skipped`} />
            <Stat label="Last 7 days" value={fmt(totals.week.sent)} sub={`${fmt(totals.week.ok)} ok · ${fmt(totals.week.failed)} failed`} />
            <Stat label="Last 30 days" value={fmt(totals.month.sent)} sub={`${fmt(totals.month.ok)} ok · ${fmt(totals.month.failed)} failed`} />
            <Stat label="Gateway cost 30d" value={`৳${fmt(totals.cost)}`} sub={`Last message ${when(totals.lastAt)}`} />
          </div>
        ) : null}

        {/* Filters */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'inline-flex', gap: 4, padding: 4, background: '#eef0f3', border: `1px solid ${C.border}`, borderRadius: 12, flexWrap: 'wrap' }}>
            {['all', 'sms', 'whatsapp'].map((c) => (
              <button key={c} type="button" onClick={() => setChannel(c)} style={{ border: 'none', borderRadius: 9, padding: '7px 14px', fontWeight: 600, background: channel === c ? '#fff' : 'transparent', boxShadow: channel === c ? '0 1px 3px rgba(0,0,0,.1)' : 'none', color: C.text }}>
                {c === 'all' ? 'All Channels' : CHANNEL_LABEL[c]}{' '}
                <span style={{ background: '#e5e7eb', borderRadius: 999, padding: '1px 8px', fontSize: 12, marginLeft: 4 }}>{counts[c]}</span>
              </button>
            ))}
          </div>
          <div style={{ position: 'relative', flex: '0 1 360px', minWidth: 200 }}>
            <i className="fas fa-search" aria-hidden="true" style={{ position: 'absolute', left: 14, top: 12, color: C.muted }} />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search templates..." style={{ width: '100%', padding: '9px 14px 9px 38px', borderRadius: 10, border: `1px solid ${C.border}` }} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 6, marginBottom: 16 }}>
          {['all', ...groups].map((g) => (
            <button key={g} type="button" onClick={() => setGroup(g)} style={{ border: 'none', borderRadius: 10, padding: '7px 14px', whiteSpace: 'nowrap', fontWeight: 500, background: group === g ? '#111827' : '#eef0f3', color: group === g ? '#fff' : '#374151' }}>
              {g === 'all' ? 'All Groups' : g}
            </button>
          ))}
        </div>

        {!data ? (
          <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 }}>Loading…</div>
        ) : (
          <>
            {/* Live status table */}
            <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 14, marginBottom: 24, overflow: 'hidden' }}>
              <button type="button" onClick={() => setTableOpen((o) => !o)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '14px 20px', border: 'none', background: '#fff', textAlign: 'left', flexWrap: 'wrap' }}>
                <i className={`fas fa-chevron-${tableOpen ? 'down' : 'right'}`} aria-hidden="true" />
                <span style={{ fontWeight: 700, fontSize: 17 }}>Messages — live status</span>
                <span style={{ color: C.muted }}>
                  {onCount}/{templates.length} on · {fmt(totals?.today?.sent)} sent today
                </span>
              </button>
              {tableOpen ? (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                    <thead>
                      <tr style={{ background: '#f9fafb', borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
                        <th style={{ padding: '10px 20px', fontSize: 13, textTransform: 'uppercase', color: '#374151' }}>Message</th>
                        <th style={{ padding: '10px 12px', fontSize: 13, textTransform: 'uppercase', color: '#374151' }}><i className="far fa-comment-alt" /> SMS</th>
                        <th style={{ padding: '10px 12px', fontSize: 13, textTransform: 'uppercase', color: '#374151' }}><i className="fab fa-whatsapp" /> WhatsApp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rowGroups.map((g) => (
                        <React.Fragment key={g.group}>
                          <tr style={{ background: '#f3f4f6' }}>
                            <td colSpan={3} style={{ padding: '6px 20px', fontWeight: 600, fontSize: 14 }}>
                              {g.group} <span style={{ color: C.muted, fontWeight: 400 }}>({g.rows.length})</span>
                            </td>
                          </tr>
                          {g.rows.map((r) => (
                            <tr key={r.base} style={{ borderBottom: `1px solid ${C.border}` }}>
                              <td style={{ padding: '10px 20px' }}>
                                <div style={{ fontWeight: 500 }}>{r.label}</div>
                                <div style={{ color: C.muted, fontSize: 13 }}>{r.base} · {r.recipient}</div>
                              </td>
                              {['sms', 'whatsapp'].map((ch) => {
                                const t = r[ch];
                                if (!t) return <td key={ch} style={{ padding: '10px 12px', color: C.muted }}>—</td>;
                                return (
                                  <td key={ch} style={{ padding: '10px 12px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <Toggle size="sm" on={t.enabled} disabled={busyKey === t.key} onClick={() => toggle(t)} />
                                      <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: t.enabled ? '#dcfce7' : '#f3f4f6', color: t.enabled ? '#166534' : C.muted }}>{t.enabled ? 'On' : 'Off'}</span>
                                    </div>
                                    <button type="button" onClick={() => setModal({ type: 'log', t })} style={{ border: 'none', background: 'none', padding: 0, marginTop: 4, fontSize: 13, color: C.muted, textDecoration: 'underline dotted' }}>
                                      {statLine(t.stats)}
                                    </button>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>

            {/* Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 360px), 1fr))', gap: 20 }}>
              {visible.map((t) => (
                <div key={t.key} style={{ background: t.enabled ? '#fff' : '#fbfbfc', border: `1px solid ${t.enabled ? '#d1d5db' : C.border}`, borderRadius: 16, padding: 20, display: 'flex', flexDirection: 'column', gap: 10, boxShadow: t.enabled ? '0 2px 8px rgba(0,0,0,.05)' : 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <ChannelBadge channel={t.channel} />
                    <Pill>{t.group}</Pill>
                    <span style={{ marginLeft: 'auto', color: t.enabled ? C.text : C.muted, fontSize: 14 }}>{t.enabled ? 'Active' : 'Inactive'}</span>
                    <Toggle on={t.enabled} disabled={busyKey === t.key} onClick={() => toggle(t)} />
                  </div>
                  <div>
                    <div style={{ fontSize: 19, fontWeight: 600, color: C.text }}>{t.label}</div>
                    <div style={{ color: C.muted, fontSize: 14 }}>{t.key} · to {t.recipient}</div>
                  </div>
                  <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', color: '#374151', fontSize: 14, minHeight: 72, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap', background: '#fff' }}>
                    {t.text || <em style={{ color: C.muted }}>Empty</em>}
                  </div>
                  <div style={{ display: 'flex', gap: 14, fontSize: 13, color: C.muted, flexWrap: 'wrap' }}>
                    <span><strong style={{ color: C.text }}>{fmt(t.stats.today.sent)}</strong> today</span>
                    <span><strong style={{ color: C.text }}>{fmt(t.stats.week.sent)}</strong> 7d</span>
                    <span><strong style={{ color: C.text }}>{fmt(t.stats.month.sent)}</strong> 30d</span>
                    {t.stats.month.failed ? <span style={{ color: '#b91c1c' }}>{fmt(t.stats.month.failed)} failed</span> : null}
                    <span>last {when(t.stats.lastAt)}</span>
                  </div>
                  <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12, marginTop: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <OutlineBtn icon="far fa-eye" onClick={() => setModal({ type: 'preview', t })}>Preview</OutlineBtn>
                    <OutlineBtn icon="far fa-paper-plane" onClick={() => setModal({ type: 'test', t })}>Test</OutlineBtn>
                    <OutlineBtn icon="fas fa-chart-bar" onClick={() => setModal({ type: 'log', t })}>Log</OutlineBtn>
                    <button type="button" onClick={() => setModal({ type: 'edit', t })} style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 18px', borderRadius: 10, border: 'none', background: C.maroon, color: '#fff', fontWeight: 600 }}>
                      <i className="fas fa-pen" aria-hidden="true" /> Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {!visible.length ? <div style={{ color: C.muted, padding: 24, textAlign: 'center' }}>No template matches.</div> : null}

            {data.unattributed?.some((u) => u.stats.month.sent) ? (
              <div style={{ color: C.muted, fontSize: 13, marginTop: 20 }}>
                Messages sent before per-template tracking (14 Oct 2026) that cannot be tied to one template:{' '}
                {data.unattributed.map((u) => `${u.type} ${fmt(u.stats.month.sent)} in 30d`).join(' · ')}.
              </div>
            ) : null}
          </>
        )}
      </div>

      {modal?.type === 'edit' ? (
        <EditModal t={modal.t} sample={data?.sample} busy={busyKey === modal.t.key} onClose={() => setModal(null)} onSave={async (text) => { if (await save(modal.t, { text })) setModal(null); }} />
      ) : null}
      {modal?.type === 'preview' ? <PreviewModal t={modal.t} sample={data?.sample} onClose={() => setModal(null)} /> : null}
      {modal?.type === 'test' ? <TestModal t={modal.t} headers={devHeaders} onClose={() => setModal(null)} /> : null}
      {modal?.type === 'log' ? <LogModal t={modal.t} headers={devHeaders} onClose={() => setModal(null)} /> : null}
    </div>
  );
}

function ModalHead({ t, title }) {
  return (
    <Modal.Header closeButton>
      <Modal.Title style={{ fontSize: 18, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <ChannelBadge channel={t.channel} /> {title}: {t.label}
      </Modal.Title>
    </Modal.Header>
  );
}

function Bubble({ channel, text }) {
  const p = smsParts(text);
  return (
    <div>
      <div style={{ background: channel === 'whatsapp' ? '#e7fbe9' : '#f1f5f9', borderRadius: 14, padding: '12px 14px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', border: `1px solid ${C.border}` }}>{text || <em>Empty</em>}</div>
      <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>
        {p.chars} characters{channel === 'sms' ? ` · ${p.parts} SMS part${p.parts > 1 ? 's' : ''}${p.unicode ? ' (Unicode)' : ''}` : ''}
      </div>
    </div>
  );
}

function EditModal({ t, sample, busy, onClose, onSave }) {
  const [text, setText] = useState(t.text);
  const ref = useRef(null);
  const insert = (v) => {
    const el = ref.current;
    const token = `{${v}}`;
    if (!el) return setText((s) => s + token);
    const { selectionStart: a, selectionEnd: b } = el;
    setText((s) => s.slice(0, a) + token + s.slice(b));
    setTimeout(() => { el.focus(); el.selectionStart = el.selectionEnd = a + token.length; }, 0);
  };
  return (
    <Modal show onHide={onClose} size="lg" centered>
      <ModalHead t={t} title="Edit" />
      <Modal.Body>
        <div style={{ color: C.muted, fontSize: 13, marginBottom: 8 }}>{t.key} · to {t.recipient}. {t.note}</div>
        <textarea ref={ref} value={text} onChange={(e) => setText(e.target.value)} rows={6} maxLength={1000} style={{ width: '100%', borderRadius: 10, border: `1px solid ${C.border}`, padding: 12, fontSize: 15 }} />
        {t.kind === 'otp' && t.channel === 'whatsapp' ? (
          <div style={{ fontSize: 12, color: C.muted }}>Leave it the same as the SMS text (or empty) to always follow the SMS text.</div>
        ) : null}
        <div style={{ margin: '10px 0' }}>
          <span style={{ fontSize: 13, color: C.muted, marginRight: 6 }}>Insert:</span>
          {t.vars.map((v) => (
            <button key={v} type="button" onClick={() => insert(v)} style={{ border: `1px solid ${C.border}`, background: '#f9fafb', borderRadius: 8, padding: '2px 8px', margin: '0 6px 6px 0', fontSize: 13, fontFamily: 'monospace' }}>{`{${v}}`}</button>
          ))}
        </div>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Preview with sample values</div>
        <Bubble channel={t.channel} text={fill(text, sample).trim()} />
      </Modal.Body>
      <Modal.Footer style={{ justifyContent: 'space-between' }}>
        <button type="button" className="btn btn-outline-secondary" disabled={!t.defaultText || text === t.defaultText} onClick={() => setText(t.defaultText)}>Reset to default</button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn btn-light" onClick={onClose}>Cancel</button>
          <button type="button" className="btn" style={{ background: C.maroon, color: '#fff' }} disabled={busy || text === t.text} onClick={() => onSave(text)}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

function PreviewModal({ t, sample, onClose }) {
  return (
    <Modal show onHide={onClose} centered>
      <ModalHead t={t} title="Preview" />
      <Modal.Body>
        <Bubble channel={t.channel} text={fill(t.text, sample).trim()} />
        <div style={{ fontSize: 12, color: C.muted, marginTop: 10 }}>Rendered with sample values. {t.note}</div>
      </Modal.Body>
    </Modal>
  );
}

function TestModal({ t, headers, onClose }) {
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(api + '/api/developer/message-templates/test', { key: t.key, phone }, { headers });
      toast.success(data.message || 'Test sent.');
      onClose();
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal show onHide={onClose} centered>
      <ModalHead t={t} title="Test" />
      <Modal.Body>
        <div style={{ fontSize: 14, color: C.muted, marginBottom: 8 }}>
          Sends this text with sample values by {CHANNEL_LABEL[t.channel]} only. Costs one real message. Works even when the template is off; the master switch still applies.
        </div>
        <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="01XXXXXXXXX" inputMode="tel" style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: `1px solid ${C.border}` }} />
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-light" onClick={onClose}>Cancel</button>
        <button type="button" className="btn" style={{ background: C.maroon, color: '#fff' }} disabled={busy || !phone.trim()} onClick={send}>{busy ? 'Sending…' : 'Send test'}</button>
      </Modal.Footer>
    </Modal>
  );
}

function LogModal({ t, headers, onClose }) {
  const [status, setStatus] = useState('all');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchPage = useCallback(async (skip) => {
    setLoading(true);
    try {
      const { data } = await axios.get(api + '/api/developer/message-templates/log', { headers, params: { key: t.key, status, limit: 100, skip } });
      setRows((cur) => (skip ? [...cur, ...data.rows] : data.rows));
      setTotal(data.total);
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setLoading(false);
    }
  }, [headers, t.key, status]);

  useEffect(() => {
    fetchPage(0);
  }, [fetchPage]);

  const s = t.stats;
  const rowState = (r) => {
    if (r.ok) return { label: 'Sent', fg: '#166534', bg: '#dcfce7' };
    if (r.skipped) return { label: 'Skipped', fg: '#92400e', bg: '#fef3c7' };
    if (r.mayHaveDelivered) return { label: 'Unconfirmed', fg: '#1e40af', bg: '#dbeafe' };
    return { label: 'Failed', fg: '#991b1b', bg: '#fee2e2' };
  };

  return (
    <Modal show onHide={onClose} size="xl" centered scrollable>
      <ModalHead t={t} title="Log" />
      <Modal.Body>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
          {[['Today', s.today], ['Last 7 days', s.week], ['Last 30 days', s.month]].map(([label, b]) => (
            <Stat key={label} label={label} value={fmt(b.sent)} sub={`${fmt(b.ok)} ok · ${fmt(b.failed)} failed · ${fmt(b.blocked)} skipped`} />
          ))}
          <Stat label="Cost 30d" value={`৳${fmt(s.cost)}`} sub={s.month.sent ? `${Math.round((s.month.ok / s.month.sent) * 100)}% success` : 'No sends'} />
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {STATUS_FILTERS.map((f) => (
            <button key={f.key} type="button" onClick={() => setStatus(f.key)} style={{ border: 'none', borderRadius: 8, padding: '5px 12px', background: status === f.key ? '#111827' : '#eef0f3', color: status === f.key ? '#fff' : '#374151' }}>{f.label}</button>
          ))}
          <span style={{ marginLeft: 'auto', color: C.muted, fontSize: 13 }}>{fmt(total)} message{total === 1 ? '' : 's'} (all time)</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="table table-sm" style={{ fontSize: 13, minWidth: 720 }}>
            <thead>
              <tr>
                <th>Time</th>
                <th>To</th>
                <th>Status</th>
                <th>Gateway</th>
                <th>Cost</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const st = rowState(r);
                return (
                  <tr key={r._id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{when(r.createdAt)}</td>
                    <td>{r.username ? <div>@{r.username}</div> : null}<div style={{ color: C.muted }}>{r.phone}</div></td>
                    <td><span style={{ padding: '2px 8px', borderRadius: 999, background: st.bg, color: st.fg, fontWeight: 600, whiteSpace: 'nowrap' }}>{st.label}</span>{r.deliveryStatus ? <div style={{ color: C.muted }}>{r.deliveryStatus}</div> : null}</td>
                    <td>{r.sessionLabel || r.provider}</td>
                    <td>{r.cost ? `৳${r.cost}` : '—'}</td>
                    <td style={{ color: C.muted, maxWidth: 380, wordBreak: 'break-word' }}>{r.detail}</td>
                  </tr>
                );
              })}
              {!rows.length && !loading ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: C.muted, padding: 20 }}>Nothing logged.</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
        {rows.length < total ? (
          <div style={{ textAlign: 'center' }}>
            <button type="button" className="btn btn-outline-secondary btn-sm" disabled={loading} onClick={() => fetchPage(rows.length)}>{loading ? 'Loading…' : 'Load more'}</button>
          </div>
        ) : null}
      </Modal.Body>
    </Modal>
  );
}
